"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, CircleCheck as CheckCircle, User, CircleAlert as AlertCircle } from 'lucide-react';
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
function NewsGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages,
    setPage
  } = usePosts();
  if (loading) {
    return <div data-section-id="161194" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
        {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="border-0 p-0 overflow-hidden bg-transparent">
            <AspectRatio ratio={16 / 9} className="bg-muted animate-pulse" />
            <div className="p-5 flex flex-col gap-3">
              <div className="flex justify-between items-center">
                <div className="h-3 w-24 bg-muted animate-pulse rounded" />
                <div className="h-4 w-16 bg-muted animate-pulse rounded" />
              </div>
              <div className="h-6 w-full bg-muted animate-pulse rounded mt-2" />
              <div className="h-6 w-2/3 bg-muted animate-pulse rounded" />
            </div>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="size-10 text-destructive mb-4" />
        <p className="text-muted-foreground mb-4">Gagal memuat berita. Silakan coba lagi.</p>
        <Button onClick={refetch} variant="outline" className="rounded-md border-primary text-primary hover:bg-primary/10">
          Coba Lagi
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-12">
        <p className="text-muted-foreground">Belum ada berita yang diterbitkan saat ini.</p>
      </div>;
  }
  return <div className="flex flex-col gap-12">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
        {posts.map((post, i) => {
        const isVerified = i % 4 === 0;
        return <Card key={post.id} data-index={i} className="border-0 border-b border-muted p-0 gap-0 overflow-hidden flex flex-col h-full hover:shadow-none bg-transparent transition-all duration-300">
              <Link to={post.link || "#"} className="block relative overflow-hidden group">
                <AspectRatio ratio={16 / 9}>
                  <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/placeholder_news.png"} alt={post.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                </AspectRatio>
              </Link>
              
              <div className="p-4 sm:p-5 lg:p-6 flex flex-col flex-grow">
                <div className="flex items-center justify-between mb-4 gap-2">
                  <div className="flex items-center gap-2 sm:gap-3 font-sans text-[11px] sm:text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <User className="size-3 sm:size-3.5" />
                      {post.author?.name || "Redaksi"}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <SafeHydrate><Calendar className="size-3 sm:size-3.5" /></SafeHydrate>
                      {post.date ? new Date(post.date).toLocaleDateString("id-ID", {
                    day: "numeric",
                    month: "short",
                    year: "numeric"
                  }) : ""}
                    </span>
                  </div>
                  
                  {isVerified && <Badge variant="secondary" className="font-sans text-[9px] sm:text-[10px] uppercase tracking-wider bg-primary/10 text-primary hover:bg-primary/20 border-none flex items-center gap-1 px-2 py-0.5 whitespace-nowrap">
                      <CheckCircle className="size-2.5 sm:size-3" />
                      Fakta Teruji
                    </Badge>}
                </div>

                <Link to={post.link || "#"} className="group flex-grow">
                  <h3 className="font-sans text-lg sm:text-xl lg:text-2xl font-semibold leading-snug text-foreground group-hover:text-primary transition-colors line-clamp-2 sm:line-clamp-3">
                    {post.title}
                  </h3>
                </Link>
              </div>
            </Card>;
      })}
      </div>

      {totalPages > 1 && <Pagination>
          <PaginationContent className="gap-2 sm:gap-1">
            <PaginationItem>
              <PaginationPrevious onClick={e => {
            e.preventDefault();
            if (hasPrev) prevPage();
          }} className={!hasPrev ? "pointer-events-none opacity-50" : "cursor-pointer hover:text-primary"} />
            </PaginationItem>
            
            {[...Array(totalPages)].map((_, p) => {
          const pageNum = p + 1;
          if (pageNum === 1 || pageNum === totalPages || pageNum >= currentPage - 1 && pageNum <= currentPage + 1) {
            return <PaginationItem key={p} data-index={p}>
                    <PaginationLink isActive={currentPage === pageNum} onClick={e => {
                e.preventDefault();
                setPage(pageNum);
              }} className={`cursor-pointer ${currentPage === pageNum ? "bg-primary text-primary-foreground" : "hover:text-primary"}`}>
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>;
          }
          if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
            return <PaginationItem key={p} data-index={p}>
                    <span className="flex size-9 items-center justify-center text-muted-foreground">
                      ...
                    </span>
                  </PaginationItem>;
          }
          return null;
        })}

            <PaginationItem>
              <PaginationNext onClick={e => {
            e.preventDefault();
            if (hasNext) nextPage();
          }} className={!hasNext ? "pointer-events-none opacity-50" : "cursor-pointer hover:text-primary"} />
            </PaginationItem>
          </PaginationContent>
        </Pagination>}
    </div>;
}
export default function NewsGrid() {
  const wp_query_params = {
    per_page: 6,
    status: "publish",
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="berita-terbaru" className="py-12 sm:py-16 md:py-20 lg:py-24 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-10">
          <h2 className="font-sans text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Berita Terbaru
          </h2>
          <p className="font-sans text-muted-foreground max-w-2xl text-base sm:text-lg lg:text-xl">
            Dapatkan akses informasi terkini dan laporan mendalam mengenai peristiwa penting yang terjadi hari ini di sekitar Anda.
          </p>
        </div>
        
        <PostsProvider wp_query={{
        per_page: 6,
        status: ["publish"],
        order: "desc",
        orderby: "date"
      }}>
          <NewsGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}